class HoverPeekVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hover-peek-video-carousel__track');
    this.items = Array.from(this.querySelectorAll('.hover-peek-video-carousel__item:not([data-carousel-clone="true"])'));
    this.isDesktop = window.matchMedia('(min-width: 741px)');
    this._onMediaChange = this._handleMediaChange.bind(this);
    this._onScroll = this._handleScroll.bind(this);

    this._bindDesktopHover();
    this._bindVideoFallbacks();
    this._handleMediaChange();
    this._setupReveal();

    if (this.isDesktop.addEventListener) {
      this.isDesktop.addEventListener('change', this._onMediaChange);
    } else {
      this.isDesktop.addListener(this._onMediaChange);
    }
  }

  disconnectedCallback() {
    this._unbindDesktopHover();
    this._teardownMobileLoop();

    if (this.isDesktop?.removeEventListener) {
      this.isDesktop.removeEventListener('change', this._onMediaChange);
    } else if (this.isDesktop?.removeListener) {
      this.isDesktop.removeListener(this._onMediaChange);
    }

    this._revealObserver?.disconnect();
  }

  _bindVideoFallbacks() {
    this.items.forEach((item) => {
      const video = item.querySelector('video');
      const cover = item.querySelector('.hover-peek-video-carousel__poster, .hover-peek-video-carousel__placeholder');

      if (!video || !cover) {
        return;
      }

      video.addEventListener('loadeddata', () => item.classList.add('has-video-ready'));
      video.addEventListener('error', () => {
        item.classList.remove('has-video-ready');
        this._resetItem(item);
      });
    });
  }

  _bindDesktopHover() {
    this._hoverBindings = this.items.map((item) => {
      const enter = () => {
        if (this.isDesktop.matches) {
          this.items.forEach((otherItem) => {
            if (otherItem !== item) {
              this._resetItem(otherItem);
            }
          });
          this._playItem(item);
        }
      };
      const leave = () => {
        if (this.isDesktop.matches) {
          this._resetItem(item);
        }
      };

      item.addEventListener('mouseenter', enter);
      item.addEventListener('mouseleave', leave);
      item.addEventListener('focusin', enter);
      item.addEventListener('focusout', leave);

      return { item, enter, leave };
    });
  }

  _unbindDesktopHover() {
    if (!this._hoverBindings) {
      return;
    }

    this._hoverBindings.forEach(({ item, enter, leave }) => {
      item.removeEventListener('mouseenter', enter);
      item.removeEventListener('mouseleave', leave);
      item.removeEventListener('focusin', enter);
      item.removeEventListener('focusout', leave);
    });
  }

  _playItem(item) {
    const video = item.querySelector('video');
    const cover = item.querySelector('.hover-peek-video-carousel__poster, .hover-peek-video-carousel__placeholder');

    if (!video) {
      return;
    }

    video.classList.add('is-playing');

    if (this.isDesktop.matches) {
      video.currentTime = 0;
    }

    video.muted = true;
    video.play()
      .then(() => {
        item.classList.add('has-video-ready');
        cover?.classList.add('is-hidden');
      })
      .catch(() => {
        this._resetItem(item);
      });
  }

  _resetItem(item) {
    const video = item.querySelector('video');
    const cover = item.querySelector('.hover-peek-video-carousel__poster, .hover-peek-video-carousel__placeholder');

    if (video) {
      video.pause();
      if (this.isDesktop.matches) {
        video.currentTime = 0;
      }

      video.classList.remove('is-playing');
    }

    cover?.classList.remove('is-hidden');
  }

  _handleMediaChange() {
    if (this.isDesktop.matches) {
      this._teardownMobileLoop();
      this.items.forEach((item) => this._resetItem(item));
      return;
    }

    this._setupMobileLoop();
  }

  _setupMobileLoop() {
    if (!this.track || this._mobileReady || this.items.length < 2) {
      return;
    }

    this._mobileReady = true;
    this._cloneCount = Math.min(2, this.items.length);
    this.items.forEach((item, index) => {
      item.dataset.carouselOriginalIndex = String(index);
    });

    const before = this.items.slice(-this._cloneCount).map((item) => this._cloneItem(item));
    const after = this.items.slice(0, this._cloneCount).map((item) => this._cloneItem(item));

    before.reverse().forEach((clone) => this.track.insertBefore(clone, this.track.firstChild));
    after.forEach((clone) => this.track.appendChild(clone));
    this._refreshMobileSlides();

    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    requestAnimationFrame(() => {
      this._scrollToItem(this.items[0], 'auto');
      this._markCenteredItem();
    });
  }

  _teardownMobileLoop() {
    if (!this.track || !this._mobileReady) {
      return;
    }

    this.track.removeEventListener('scroll', this._onScroll);
    window.clearTimeout(this._scrollEndTimer);
    if (this._scrollFrame) {
      window.cancelAnimationFrame(this._scrollFrame);
      this._scrollFrame = null;
    }
    this.track.querySelectorAll('[data-carousel-clone="true"]').forEach((clone) => clone.remove());
    this._mobileSlides = null;
    this.items.forEach((item) => {
      item.classList.remove('is-centered');
      delete item.dataset.carouselOriginalIndex;
    });
    this._mobileReady = false;
  }

  _cloneItem(item) {
    const clone = item.cloneNode(true);
    clone.dataset.carouselClone = 'true';
    clone.dataset.carouselOriginalIndex = item.dataset.carouselOriginalIndex;
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('video').forEach((video) => video.removeAttribute('src'));
    clone.querySelectorAll('a, button, input, select, textarea, video').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    return clone;
  }

  _handleScroll() {
    if (this._scrollFrame) {
      window.cancelAnimationFrame(this._scrollFrame);
    }

    this._scrollFrame = window.requestAnimationFrame(() => {
      this._markCenteredItem();
      window.clearTimeout(this._scrollEndTimer);
      this._scrollEndTimer = window.setTimeout(() => this._normalizeMobileLoopPosition(), 120);
    });
  }

  _refreshMobileSlides() {
    this._mobileSlides = Array.from(this.track.querySelectorAll('.hover-peek-video-carousel__item'));
  }

  _getMobileSlides() {
    if (!this._mobileSlides) {
      this._refreshMobileSlides();
    }

    return this._mobileSlides || this.items;
  }

  _normalizeMobileLoopPosition() {
    if (!this._mobileReady || this.isDesktop.matches) {
      return;
    }

    const centeredItem = this._centeredItem || this._getClosestItem(this._getMobileSlides());
    const originalIndex = Number(centeredItem?.dataset.carouselOriginalIndex);

    if (centeredItem?.dataset.carouselClone === 'true' && Number.isInteger(originalIndex) && this.items[originalIndex]) {
      this._scrollToItem(this.items[originalIndex], 'auto');
      requestAnimationFrame(() => this._markCenteredItem());
      return;
    }

    const firstReal = this.items[0];
    const lastReal = this.items[this.items.length - 1];

    if (!firstReal || !lastReal) {
      return;
    }

    const center = this.track.scrollLeft + this.track.clientWidth / 2;
    const firstCenter = firstReal.offsetLeft + firstReal.offsetWidth / 2;
    const lastCenter = lastReal.offsetLeft + lastReal.offsetWidth / 2;
    const step = this.items[1] ? this.items[1].offsetLeft - this.items[0].offsetLeft : firstReal.offsetWidth;

    if (center < firstCenter - step * this._cloneCount) {
      this._scrollToItem(lastReal, 'auto');
    } else if (center > lastCenter + step * this._cloneCount) {
      this._scrollToItem(firstReal, 'auto');
    }
  }

  _getClosestItem(items) {
    const center = this.track.scrollLeft + this.track.clientWidth / 2;
    let closestItem = null;
    let closestDistance = Infinity;

    items.forEach((item) => {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const distance = Math.abs(center - itemCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = item;
      }
    });

    return closestItem;
  }

  _markCenteredItem() {
    const items = !this.isDesktop.matches && this._mobileReady ? this._getMobileSlides() : this.items;
    const closestItem = this._getClosestItem(items);
    this._centeredItem = closestItem;

    items.forEach((item) => {
      const isCentered = item === closestItem;
      item.classList.toggle('is-centered', isCentered);

      if (!this.isDesktop.matches) {
        if (isCentered) {
          this._playItem(item);
        } else {
          this._resetItem(item);
        }
      }
    });
  }

  _scrollToItem(item, behavior = 'smooth') {
    const left = item.offsetLeft - (this.track.clientWidth - item.offsetWidth) / 2;
    this.track.scrollTo({ left, behavior });
  }

  _setupReveal() {
    if (this.dataset.revealBound === 'true') {
      return;
    }

    this.dataset.revealBound = 'true';
    this.classList.add('is-reveal-ready');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      this.classList.add('is-visible');
      return;
    }

    this._revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.classList.add('is-visible');
            this._revealObserver?.disconnect();
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.16 }
    );
    this._revealObserver.observe(this);
  }
}

if (!customElements.get('hover-peek-video-carousel')) {
  customElements.define('hover-peek-video-carousel', HoverPeekVideoCarousel);
}
